import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Mail, Phone, MapPin, Clock, Loader2, CheckCircle } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { t } = useLanguage();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message) {
      toast.error(t.booking.required);
      return;
    }

    setSubmitting(true);

    try {
      await axios.post(`${API}/contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        message: formData.message,
      });

      setSubmitted(true);
      toast.success(t.contact.successMessage);
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      label: t.contact.emailUs,
      value: "info@magnoliaeventsandrentals.com",
      href: "mailto:info@magnoliaeventsandrentals.com",
    },
    {
      icon: Phone,
      label: t.contact.callUs,
      value: "(561) 501-1241",
      href: "tel:+15615011241",
    },
    {
      icon: MapPin,
      label: t.contact.visitUs,
      value: "6268 Floridian Cir, Lake Worth, FL 33463",
    },
  ];

  return (
    <div className="py-12 lg:py-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="mb-12">
          <h1 className="font-heading font-bold text-4xl sm:text-5xl text-charcoal mb-4">
            {t.contact.title}
          </h1>
          <p className="text-lg text-charcoal/70 max-w-2xl">
            {t.contact.subtitle}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-white rounded-3xl border border-charcoal/10 p-8 shadow-sm">
            {submitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="font-heading font-bold text-2xl text-charcoal mb-2">
                  {t.contact.successMessage}
                </h2>
                <Button
                  data-testid="send-another-btn"
                  onClick={() => {
                    setSubmitted(false);
                    setFormData({ name: "", email: "", phone: "", message: "" });
                  }}
                  variant="outline"
                  className="rounded-full mt-4"
                >
                  {t.contact.send}
                </Button>
              </div>
            ) : (
              <>
                <h2 className="font-heading font-bold text-2xl text-charcoal mb-6">
                  {t.contact.sendMessage}
                </h2>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <Label htmlFor="name" className="text-charcoal/70 mb-2 block">
                      {t.contact.name} *
                    </Label>
                    <Input
                      id="name"
                      data-testid="contact-name"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, name: e.target.value }))
                      }
                      placeholder={t.contact.namePlaceholder}
                      className="h-12 rounded-xl"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-charcoal/70 mb-2 block">
                      {t.contact.email} *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      data-testid="contact-email"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, email: e.target.value }))
                      }
                      placeholder={t.contact.emailPlaceholder}
                      className="h-12 rounded-xl"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-charcoal/70 mb-2 block">
                      {t.contact.phone}
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      data-testid="contact-phone"
                      value={formData.phone}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, phone: e.target.value }))
                      }
                      placeholder={t.contact.phonePlaceholder}
                      className="h-12 rounded-xl"
                    />
                  </div>
                  <div>
                    <Label htmlFor="message" className="text-charcoal/70 mb-2 block">
                      {t.contact.message} *
                    </Label>
                    <Textarea
                      id="message"
                      data-testid="contact-message"
                      value={formData.message}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          message: e.target.value,
                        }))
                      }
                      placeholder={t.contact.messagePlaceholder}
                      className="rounded-xl resize-none"
                      rows={5}
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    data-testid="contact-submit-btn"
                    disabled={submitting}
                    className="w-full bg-sage hover:bg-sage/90 text-white rounded-full py-6 text-lg font-bold btn-hover"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        {t.contact.sending}
                      </>
                    ) : (
                      t.contact.send
                    )}
                  </Button>
                </form>
              </>
            )}
          </div>

          {/* Contact Info */}
          <div className="space-y-8">
            <div className="bg-cream rounded-3xl p-8">
              <h2 className="font-heading font-bold text-2xl text-charcoal mb-6">
                {t.contact.getInTouch}
              </h2>
              <div className="space-y-6">
                {contactInfo.map((item) => (
                  <div key={item.label} className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-sage/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-sage" />
                    </div>
                    <div>
                      <p className="text-sm text-charcoal/60 mb-1">{item.label}</p>
                      {item.href ? (
                        <a
                          href={item.href}
                          className="font-medium text-charcoal hover:text-sage transition-colors"
                        >
                          {item.value}
                        </a>
                      ) : (
                        <p className="font-medium text-charcoal">{item.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
